import { cancelChatMessages, sendChatMessages } from "../api/message";
import {
  clearActiveThreadDispatch,
  getActiveThreadDispatch,
  hasActiveThreadDispatch,
  setActiveThreadDispatch,
} from "../components/Chat/chatDispatchRegistry";
import {
  appendThreadMessage,
  localMessageMatches,
  patchThreadMessage,
  removeThreadMessage,
} from "../components/Chat/chatThreadMessagesModel";
import {
  createQueuedChatMessage,
  DEFAULT_MAX_QUEUED_MESSAGES_PER_THREAD,
  QUEUED_MESSAGE_STATUS,
  queuedMessagesForThread,
} from "../components/Chat/queuedChatMessages";
import {
  chatSartingQuestions,
  expiredSessionMessage,
  questionRegularText,
  requireCreateAccountMessage,
  requireLoginMessage,
} from "../utils/constants";
import { filesToAttachments } from "../utils/images_uploads_attachments";
import {
  clearRememberedActiveChatThread,
  rememberActiveChatThread,
} from "../utils/chat_thread_persistence";
import {
  pendingSecondQuestionEventMetadata,
  writePendingSecondQuestion,
} from "../utils/signup_modal";
import { trackFrontendEvent } from "../utils/tracking";
import { isMobileIOS } from "../utils/user_agent";

const QUEUED_MESSAGE_DRAIN_DELAY_MS = 80;

const hasText = (value) => String(value || "").trim().length > 0;

const normalizedQuestion = (question) =>
  String(question || "").trim().replace(/\s+/g, " ");

const isSubmittedHumanMessage = (message) =>
  message?.sender === "human" && !message?.isButton && hasText(message?.message);

const messageHasChart = (message) =>
  (Array.isArray(message?.dates) && message.dates.length > 0) ||
  (Array.isArray(message?.values1) && message.values1.length > 0) ||
  (Array.isArray(message?.values_1) && message.values_1.length > 0);

const firstCompletedAnswerAfterHuman = (messages = []) => {
  let sawSubmittedQuestion = false;

  for (const message of messages || []) {
    if (isSubmittedHumanMessage(message)) {
      sawSubmittedQuestion = true;
      continue;
    }

    if (sawSubmittedQuestion && message?.sender === "bot" && hasText(message?.message)) {
      return message;
    }
  }

  return null;
};

const conversionGateFromResponse = (response = {}) =>
  response?.conversionGate || response?.conversion_gate || null;

const useChatDispatch = ({
  activeChatThreadPersistenceScope,
  activeThread,
  activeThreadRef,
  adminBaseDirectiveModelOverride,
  adminBaseDirectiveReasoningOverride,
  appProps,
  checkThing,
  clearDataProcessesForThread,
  deviceType,
  enqueueQueuedMessageForThread,
  ensureThreadSubscription,
  inFlightSendByThreadRef,
  isLoggedIn,
  loadingResponse,
  loadingResponseByThreadUuid,
  markQueuedMessageForThread,
  messageThreads,
  messageThreadsOwnerRef,
  messagesByThreadUuidRef,
  navigate,
  preventQuestionInput,
  preventQuestionInputByThreadUuid,
  question,
  queuedMessagesByThreadUuidRef,
  removeQueuedMessageForThread,
  resolveTempId,
  resolvedTempThreadMapRef,
  restoredPendingSecondQuestionRef,
  setActiveThread,
  setButtonOpacity,
  setCancelRequested,
  setConversionGateContext,
  setFreeUnlimitedOnly,
  setHasShownFreeUnlimitedMessage,
  setIdSelected,
  setIsSecondaryExperience,
  setLiveTextOutputting,
  setLoadingResponse,
  setMessageThreads,
  setOpacity,
  setPaywallConversionGateContext,
  setPaywallMessage,
  setPreventQuestionInput,
  setPreventScroll,
  setProBannerSessionQuestionCount,
  setQuestion,
  setRegisterNavMessage,
  setReferralProgram,
  setResponseFlow,
  setResponseFlowForThread,
  setServiceTipPrompt,
  setShowAddHomeModal,
  setShowChatShare,
  setShowLoadingWaitingMessage,
  setShowPastDueModal,
  setShowPaywallModal,
  setShowReferralInviteModal,
  setShowReviewModal,
  setShowServiceTipModal,
  setShowSignUpModal,
  setShowSubOfferModal,
  setShowTextSetupModal,
  setTestResponseMessage,
  setTextSetupContext,
  setThreadLiveTextOutputting,
  setThreadLoadingPhaseDetail,
  setThreadLoadingPhaseMessage,
  setThreadLoadingResponse,
  setThreadPreventQuestionInput,
  setThreadQuestionOnDemand,
  setThreadShowLoadingWaitingMessage,
  setThreadTestResponseMessage,
  setThreadThing,
  setThreads,
  setUserData,
  shiftQueuedMessageForThread,
  updateQueuedMessageForThread,
  updateThreadMessages,
  userData,
}) => {
  const clearDispatchRuntimeState = (threadUuid, options = {}) => {
    const {
      clearPreventInput = true,
      clearLiveText = true,
    } = options || {};

    if (threadUuid) {
      setThreadLoadingResponse(threadUuid, false);
      setThreadShowLoadingWaitingMessage(threadUuid, 0);
      setThreadLoadingPhaseMessage(threadUuid, null);
      setThreadLoadingPhaseDetail(threadUuid, null);
      if (clearLiveText) {
        setThreadLiveTextOutputting(threadUuid, false);
      }
      if (clearPreventInput) {
        setThreadPreventQuestionInput(threadUuid, false);
      }
      return;
    }

    setLoadingResponse(false);
    setShowLoadingWaitingMessage(0);
    if (clearLiveText) {
      setLiveTextOutputting(false);
    }
    if (clearPreventInput) {
      setPreventQuestionInput(false);
    }
  };

  const appendMessage = (threadUuid, msg) => {
    if (!threadUuid) return;
    updateThreadMessages(threadUuid, (prev) => appendThreadMessage(prev, msg));
  };

  const threadHasActiveDispatch = (threadUuid) =>
    Boolean(
      (threadUuid
        ? loadingResponseByThreadUuid?.[threadUuid]
        : loadingResponse) ||
        hasActiveThreadDispatch(inFlightSendByThreadRef.current, threadUuid),
    );

  const syncQueuedMessagesRef = (threadUuid, nextQueue) => {
    const nextMap = { ...(queuedMessagesByThreadUuidRef.current || {}) };
    if (threadUuid && nextQueue.length > 0) {
      nextMap[threadUuid] = nextQueue;
    } else if (threadUuid) {
      delete nextMap[threadUuid];
    }
    queuedMessagesByThreadUuidRef.current = nextMap;
  };

  const moveQueuedMessagesByThreadUuidRef = (fromThreadUuid, toThreadUuid) => {
    if (!fromThreadUuid || !toThreadUuid || fromThreadUuid === toThreadUuid) return;
    const currentMap = queuedMessagesByThreadUuidRef.current || {};
    const fromQueue = queuedMessagesForThread(currentMap, fromThreadUuid);
    if (fromQueue.length === 0) return;
    const toQueue = queuedMessagesForThread(currentMap, toThreadUuid);
    const nextMap = { ...currentMap };
    nextMap[toThreadUuid] = toQueue.concat(fromQueue);
    delete nextMap[fromThreadUuid];
    queuedMessagesByThreadUuidRef.current = nextMap;
  };

  const enqueueUserQuestionForThread = (
    threadUuid,
    messageText,
    messageId,
    tickerIds,
    imagesMeta = [],
    options = {},
  ) => {
    const hasQueuedPayload =
      hasText(messageText) ||
      (Array.isArray(imagesMeta) && imagesMeta.length > 0);
    if (!threadUuid || !hasQueuedPayload) return false;

    const currentQueue = queuedMessagesForThread(
      queuedMessagesByThreadUuidRef.current,
      threadUuid,
    );
    if (currentQueue.length >= DEFAULT_MAX_QUEUED_MESSAGES_PER_THREAD) {
      return false;
    }

    const queuedMessage = createQueuedChatMessage({
      message: messageText,
      attachments: filesToAttachments(imagesMeta),
      extra: {
        dispatchPayload: {
          message: messageText,
          messageId,
          tickerIds,
          imagesMeta,
          options,
        },
        queuedAt: new Date().toISOString(),
      },
    });

    syncQueuedMessagesRef(threadUuid, currentQueue.concat(queuedMessage));
    enqueueQueuedMessageForThread(threadUuid, queuedMessage);
    return true;
  };

  const removeQueuedMessageFromThread = (threadUuid, clientQueueId) => {
    if (!threadUuid || !clientQueueId) return;
    const nextQueue = queuedMessagesForThread(
      queuedMessagesByThreadUuidRef.current,
      threadUuid,
    ).filter((item) => item?.clientQueueId !== clientQueueId);
    syncQueuedMessagesRef(threadUuid, nextQueue);
    removeQueuedMessageForThread(threadUuid, clientQueueId);
    updateThreadMessages(threadUuid, (prev) =>
      removeThreadMessage(prev, clientQueueId),
    );
  };

  const patchQueuedMessageInThread = (threadUuid, clientQueueId, patch = {}) => {
    if (!threadUuid || !clientQueueId) return;
    updateThreadMessages(threadUuid, (prev) =>
      patchThreadMessage(prev, clientQueueId, patch),
    );
  };

  const patchLocalMessageSendState = (threadUuid, targetId, patch = {}) => {
    if (targetId == null || targetId === "") return;

    if (threadUuid) {
      updateThreadMessages(threadUuid, (prev) =>
        patchThreadMessage(prev, targetId, patch),
      );
      return;
    }

    setMessageThreads((prev) => patchThreadMessage(prev, targetId, patch));
  };

  const markQueuedMessageInThread = (threadUuid, clientQueueId, queueStatus, extra = {}) => {
    if (!threadUuid || !clientQueueId || !queueStatus) return;
    const currentQueue = queuedMessagesForThread(
      queuedMessagesByThreadUuidRef.current,
      threadUuid,
    );
    let changed = false;
    const nextQueue = currentQueue.map((item) => {
      if (item?.clientQueueId !== clientQueueId) return item;
      changed = true;
      return {
        ...item,
        ...extra,
        queueStatus,
      };
    });
    if (changed) {
      syncQueuedMessagesRef(threadUuid, nextQueue);
    }
    markQueuedMessageForThread(threadUuid, clientQueueId, queueStatus, extra);
    patchQueuedMessageInThread(threadUuid, clientQueueId, {
      ...extra,
      queueStatus,
    });
  };

  const editQueuedMessageForThread = (threadUuid, clientQueueId, newText) => {
    const cleaned = (newText ?? "").trim();
    if (!threadUuid || !clientQueueId || !cleaned) return false;

    const currentQueue = queuedMessagesForThread(
      queuedMessagesByThreadUuidRef.current,
      threadUuid,
    );
    const target = currentQueue.find(
      (item) => item?.clientQueueId === clientQueueId,
    );
    if (!target || target.queueStatus !== QUEUED_MESSAGE_STATUS.QUEUED) {
      return false;
    }

    const nextPayload = {
      ...(target.dispatchPayload || {}),
      message: cleaned,
    };
    const patch = {
      message: cleaned,
      dispatchPayload: nextPayload,
      queueError: null,
    };
    const nextQueue = currentQueue.map((item) =>
      item?.clientQueueId === clientQueueId
        ? { ...item, ...patch }
        : item,
    );

    syncQueuedMessagesRef(threadUuid, nextQueue);
    updateQueuedMessageForThread?.(threadUuid, clientQueueId, patch);
    patchQueuedMessageInThread(threadUuid, clientQueueId, patch);
    return true;
  };

  const completeQueuedMessageInThread = (threadUuid, clientQueueId) => {
    if (!threadUuid || !clientQueueId) return;
    const currentQueue = queuedMessagesForThread(
      queuedMessagesByThreadUuidRef.current,
      threadUuid,
    );
    const isHead = currentQueue[0]?.clientQueueId === clientQueueId;
    const nextQueue = isHead
      ? currentQueue.slice(1)
      : currentQueue.filter((item) => item?.clientQueueId !== clientQueueId);
    syncQueuedMessagesRef(threadUuid, nextQueue);
    if (isHead) {
      shiftQueuedMessageForThread(threadUuid, clientQueueId);
    } else {
      removeQueuedMessageForThread(threadUuid, clientQueueId);
    }
    patchQueuedMessageInThread(threadUuid, clientQueueId, {
      queueStatus: null,
      queueError: null,
      dispatchPayload: null,
      shouldAnchor: true,
    });
  };

  const failQueuedMessageInThread = (
    threadUuid,
    clientQueueId,
    queueError = "Message was not sent.",
  ) => {
    if (!threadUuid || !clientQueueId) return;
    const currentQueue = queuedMessagesForThread(
      queuedMessagesByThreadUuidRef.current,
      threadUuid,
    );
    const nextQueue = currentQueue.filter(
      (item) => item?.clientQueueId !== clientQueueId,
    );
    syncQueuedMessagesRef(threadUuid, nextQueue);
    removeQueuedMessageForThread(threadUuid, clientQueueId);
    patchQueuedMessageInThread(threadUuid, clientQueueId, {
      queueStatus: QUEUED_MESSAGE_STATUS.FAILED,
      queueError,
      shouldAnchor: false,
    });
  };

  const handleAbortResponse = async () => {
    if (!loadingResponse) return;

    setCancelRequested(true);

    const threadUuid = activeThread?.threadUuid;
    if (threadUuid) {
      cancelChatMessages(threadUuid);
    }

    const pendingRequest = getActiveThreadDispatch(
      inFlightSendByThreadRef.current,
      threadUuid,
    );
    if (pendingRequest?.controller) {
      try {
        pendingRequest.controller.abort();
      } catch {}
    }
    if (pendingRequest?.queuedClientQueueId) {
      failQueuedMessageInThread(
        threadUuid,
        pendingRequest.queuedClientQueueId,
        "Stopped before completion.",
      );
    }
    inFlightSendByThreadRef.current = clearActiveThreadDispatch(
      inFlightSendByThreadRef.current,
      threadUuid,
    ).registry;

    clearDispatchRuntimeState(threadUuid);
  };

  const dispatchUserQuestion = (
    q = null,
    messageId = null,
    ticker_ids = null,
    imagesMeta = [],
    options = {},
  ) => {
    setIsSecondaryExperience(false);
    let threadUuid =
      activeThreadRef.current?.threadUuid ?? activeThread?.threadUuid ?? null;
    const activeThreadUuidAtSubmit = threadUuid;
    const requestedThreadUuid = threadUuid;
    if (requestedThreadUuid?.startsWith("temp-")) {
      const mappedRealUuid =
        resolvedTempThreadMapRef.current?.[requestedThreadUuid];
      if (mappedRealUuid) {
        threadUuid = mappedRealUuid;
        if (resolvedTempThreadMapRef.current?.[requestedThreadUuid]) {
          delete resolvedTempThreadMapRef.current[requestedThreadUuid];
        }
      }
    }
    if (threadUuid) {
      rememberActiveChatThread(
        {
          threadUuid,
          title: activeThreadRef.current?.title || activeThread?.title || "Chat",
          pendingRealThread: threadUuid.startsWith("temp-"),
        },
        activeChatThreadPersistenceScope,
      );
      ensureThreadSubscription(threadUuid, {
        setAsPrimary: true,
      });
    }
    const {
      editedMessageId: legacyEditedMessageId = null,
      editAnchorMessageId: explicitEditAnchorMessageId = null,
      tradeProposalId = null,
      suppressLocalUserAppend = false,
      overrideMessage = null,
      clientQueueId = null,
      fromQueue = false,
      adminBaseDirectiveModelName = adminBaseDirectiveModelOverride || null,
      adminBaseDirectiveReasoningEffort = adminBaseDirectiveReasoningOverride || null,
    } = options || {};
    const editAnchorMessageId = explicitEditAnchorMessageId ?? legacyEditedMessageId;
    const messageText = q ?? question;
    const normalizedMessageText = String(messageText || "");
    const hasMessagePayload =
      hasText(normalizedMessageText) ||
      (Array.isArray(imagesMeta) && imagesMeta.length > 0);
    let isExplainWhy = normalizedMessageText == "Explain why?";
    const dispatchPayload = {
      message: normalizedMessageText,
      messageId,
      tickerIds: ticker_ids,
      imagesMeta: Array.isArray(imagesMeta) ? imagesMeta : [],
      options: {
        editAnchorMessageId,
        tradeProposalId,
        adminBaseDirectiveModelName,
        adminBaseDirectiveReasoningEffort,
      },
    };

    if (userData.requireSignIn) {
      if (userData.uuidHasAccount) {
        appProps.setRegisterNavMessage(requireLoginMessage);
      } else {
        appProps.setRegisterNavMessage(requireCreateAccountMessage);
      }
      navigate("/register");
      return false;
    }

    if (isLoggedIn && userData.is_email_confirmed == false) {
      navigate("/confirm-email");
      return false;
    }

    const isPrevented = threadUuid
      ? Boolean(preventQuestionInputByThreadUuid?.[threadUuid])
      : preventQuestionInput;
    const queueThreadUuid = activeThreadUuidAtSubmit || threadUuid;
    const hasActiveDispatchForThread = Boolean(
      (threadUuid && threadHasActiveDispatch(threadUuid)) ||
        (
          queueThreadUuid &&
          queueThreadUuid !== threadUuid &&
          threadHasActiveDispatch(queueThreadUuid)
        ),
    );
    if (
      !fromQueue &&
      !editAnchorMessageId &&
      queueThreadUuid &&
      hasMessagePayload &&
      hasActiveDispatchForThread
    ) {
      const queued = enqueueUserQuestionForThread(
        queueThreadUuid,
        normalizedMessageText,
        messageId,
        ticker_ids,
        imagesMeta,
        options,
      );
      if (queued) {
        setCancelRequested(false);
        setQuestion("");
        setPreventScroll(false);
      }
      return queued;
    }
    if ((isPrevented || hasActiveDispatchForThread) && !fromQueue) {
      return false;
    }
    if (threadUuid) {
      setThreadPreventQuestionInput(threadUuid, true);
    } else if (!preventQuestionInput) {
      setPreventQuestionInput(true);
    }
    const inFlightLocalMessageFields = clientQueueId
      ? {
          clientQueueId,
          dispatchPayload,
          queueStatus: QUEUED_MESSAGE_STATUS.SENDING,
        }
      : {};
    let message;
    if (overrideMessage) {
      message = {
        ...overrideMessage,
        ...inFlightLocalMessageFields,
        shouldAnchor: true,
        attachments: Array.isArray(overrideMessage.attachments)
          ? overrideMessage.attachments
          : filesToAttachments(imagesMeta),
      };
    } else if (q != null) {
      setIdSelected(messageId);
      message = {
        id: Date.now(),
        sender: "human",
        message: normalizedMessageText,
        createdAt: new Date(),
        shouldAnchor: true,
        attachments: filesToAttachments(imagesMeta),
        ...inFlightLocalMessageFields,
      };
      setIdSelected(messageId);
    } else {
      message = {
        id: Date.now(),
        sender: "human",
        message: normalizedMessageText,
        createdAt: new Date(),
        shouldAnchor: true,
        attachments: filesToAttachments(imagesMeta),
        ...inFlightLocalMessageFields,
      };
    }
    const restoredPendingSecondQuestion = restoredPendingSecondQuestionRef.current;
    const isRestoredPendingSecondQuestion =
      restoredPendingSecondQuestion?.question &&
      normalizedQuestion(restoredPendingSecondQuestion.question) === normalizedQuestion(message.message);

    if (
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "staging.inciteai.com" &&
      typeof window.fbq === "function" &&
      !userData.is_admin_user
    ) {
      window.fbq("trackCustom", "questionAsked");
    }

    if (threadUuid) {
      setResponseFlowForThread(threadUuid, (responseFlow) => ({
        ...responseFlow,
        review: false,
        line: false,
        text: false,
        question1: [false, responseFlow.question1[1], responseFlow.question1[2], responseFlow.question1[3], responseFlow.question1[4], responseFlow.question1[5]],
        question2: [false, responseFlow.question2[1], responseFlow.question2[2], responseFlow.question2[3], responseFlow.question2[4], responseFlow.question2[5]],
        regen: false,
      }));
    } else {
      setResponseFlow((responseFlow) => ({
        ...responseFlow,
        review: false,
        line: false,
        text: false,
        question1: [false, responseFlow.question1[1], responseFlow.question1[2], responseFlow.question1[3], responseFlow.question1[4], responseFlow.question1[5]],
        question2: [false, responseFlow.question2[1], responseFlow.question2[2], responseFlow.question2[3], responseFlow.question2[4], responseFlow.question2[5]],
        regen: false,
      }));
    }

    let hasReceivedResponse = false;
    setButtonOpacity(0.0);
    setPreventScroll(true);
    setCancelRequested(false);
    const shouldAppendLocalMessage = !(suppressLocalUserAppend && clientQueueId);
    if (threadUuid) {
      clearDataProcessesForThread(threadUuid);
      if (shouldAppendLocalMessage) {
        appendMessage(threadUuid, message);
      }
    } else if (shouldAppendLocalMessage) {
      setMessageThreads((prev) => [...prev, message]);
    }
    if (
      hasText(message.message) ||
      (Array.isArray(message.attachments) && message.attachments.length > 0)
    ) {
      setProBannerSessionQuestionCount((count) => count + 1);
    }
    if (!hasReceivedResponse) {
      setQuestion("");
      if (threadUuid) {
        setThreadLoadingResponse(threadUuid, true);
        setThreadShowLoadingWaitingMessage(threadUuid, 0);
        setThreadLoadingPhaseMessage(threadUuid, null);
        setThreadLoadingPhaseDetail(threadUuid, null);
        setThreadLiveTextOutputting(threadUuid, false);
        setThreadQuestionOnDemand(threadUuid, false);
      } else {
        setLoadingResponse(true);
      }
      setPreventScroll(false);
    }

    let send_message = message.message;
    send_message = send_message + questionRegularText;
    console.log("about to request...");
    const requestToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const priorRequest = getActiveThreadDispatch(
      inFlightSendByThreadRef.current,
      threadUuid,
    );
    if (priorRequest?.controller) {
      try {
        priorRequest.controller.abort();
      } catch {}
    }
    if (priorRequest?.queuedClientQueueId) {
      failQueuedMessageInThread(
        threadUuid,
        priorRequest.queuedClientQueueId,
        "Stopped before completion.",
      );
    }
    const requestController =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    let dispatchThreadUuid = threadUuid;
    let shouldDrainQueuedMessages = true;
    let suppressedResponseActionsForQueue = false;
    let queuedFailureMessage = "Message did not complete.";
    const queuedClientQueueId = clientQueueId || null;
    inFlightSendByThreadRef.current = setActiveThreadDispatch(
      inFlightSendByThreadRef.current,
      threadUuid,
      {
        requestToken,
        controller: requestController,
        queuedClientQueueId,
      },
    );
    let botResponse = sendChatMessages(
      send_message,
      isExplainWhy,
      ticker_ids,
      deviceType,
      "browser",
      threadUuid,
      resolveTempId,
      imagesMeta,
      editAnchorMessageId,
      tradeProposalId,
      {
        signal: requestController?.signal,
        adminBaseDirectiveModelName,
        adminBaseDirectiveReasoningEffort,
      },
    );

    botResponse
      .then((botResponse) => {
        const resolvedThreadUuid = botResponse?.threadUuid || threadUuid;
        dispatchThreadUuid = resolvedThreadUuid;
        hasReceivedResponse = true;
        let response_message;
        let questions_prepopulated;
        if (botResponse && botResponse.error) {
          if (botResponse.retryableTransportError) {
            shouldDrainQueuedMessages = false;
            const retryError =
              botResponse.error || "Message was not sent. Check your connection and retry.";
            queuedFailureMessage = retryError;

            if (!queuedClientQueueId) {
              const localRetryTarget =
                message.clientQueueId || message.id || message.uuid;
              patchLocalMessageSendState(resolvedThreadUuid, localRetryTarget, {
                queueStatus: QUEUED_MESSAGE_STATUS.FAILED,
                queueError: retryError,
                dispatchPayload,
                shouldAnchor: false,
              });
            }

            if (resolvedThreadUuid) {
              clearDispatchRuntimeState(resolvedThreadUuid);
            } else {
              clearDispatchRuntimeState(null, { clearLiveText: false });
            }
            setOpacity(0.0);
            setOpacity(1.0);
            setButtonOpacity(0.999);
            return;
          }

          if (botResponse.error == "Session Expired") {
            shouldDrainQueuedMessages = false;
            setRegisterNavMessage(expiredSessionMessage);
            navigate("/register");
            if (resolvedThreadUuid) {
              updateThreadMessages(resolvedThreadUuid, (messageThreads) => [
                ...messageThreads.slice(0, -1),
              ]);
              setResponseFlowForThread(resolvedThreadUuid, (responseFlow) => ({
                ...responseFlow,
                question1: [true, message.message, null, false, null],
                question2: [
                  true,
                  chatSartingQuestions[1].message,
                  null,
                  botResponse.isAdvanced,
                  botResponse.advancedType,
                ],
                regen: true,
              }));
              setThreadLoadingResponse(resolvedThreadUuid, false);
              setThreadShowLoadingWaitingMessage(resolvedThreadUuid, 0);
              setThreadLoadingPhaseMessage(resolvedThreadUuid, null);
              setThreadLoadingPhaseDetail(resolvedThreadUuid, null);
              setThreadLiveTextOutputting(resolvedThreadUuid, false);
              setThreadPreventQuestionInput(resolvedThreadUuid, false);
            } else {
              setMessageThreads((messageThreads) => [
                ...messageThreads.slice(0, -1),
              ]);
              setResponseFlow((responseFlow) => ({
                ...responseFlow,
                question1: [true, message.message, null, false, null],
                question2: [
                  true,
                  chatSartingQuestions[1].message,
                  null,
                  botResponse.isAdvanced,
                  botResponse.advancedType,
                ],
                regen: true,
              }));
              setLoadingResponse(false);
              setShowLoadingWaitingMessage(0);
              setPreventQuestionInput(false);
            }
            return;
          }

          if (botResponse.threadNotFound) {
            shouldDrainQueuedMessages = false;
            const staleThreadUuid = botResponse.threadUuid || threadUuid;
            clearRememberedActiveChatThread(activeChatThreadPersistenceScope);
            if (staleThreadUuid) {
              setThreads((prev) =>
                (Array.isArray(prev) ? prev : []).filter(
                  (thread) => thread?.threadUuid !== staleThreadUuid,
                ),
              );
              setThreadLoadingResponse(staleThreadUuid, false);
              setThreadShowLoadingWaitingMessage(staleThreadUuid, 0);
              setThreadLoadingPhaseMessage(staleThreadUuid, null);
              setThreadLoadingPhaseDetail(staleThreadUuid, null);
              setThreadLiveTextOutputting(staleThreadUuid, false);
              setThreadPreventQuestionInput(staleThreadUuid, false);
              if (
                activeThreadRef.current?.threadUuid === staleThreadUuid ||
                activeThread?.threadUuid === staleThreadUuid
              ) {
                activeThreadRef.current = null;
                messageThreadsOwnerRef.current = null;
                setActiveThread(null);
                setMessageThreads([]);
              }
            } else {
              setLoadingResponse(false);
              setShowLoadingWaitingMessage(0);
              setPreventQuestionInput(false);
            }
            setQuestion(message.message);
            setOpacity(1.0);
            setButtonOpacity(0.999);
            return;
          }

          console.log("IN THE THEN!", botResponse);
          response_message = {
            createdAt: null,
            message: botResponse.error,
            sender: "bot",
            tickerDescription: [],
            answeredTickerIds: [],
            logoUrls: [],
            isError: true,
          };
          if (botResponse.show_share) {
            setShowChatShare(true);
          }

          if (botResponse.show_sign_up) {
            shouldDrainQueuedMessages = false;
            const conversionGate = conversionGateFromResponse(botResponse);
            const firstCompletedAnswer = firstCompletedAnswerAfterHuman(messageThreads);
            const pending = writePendingSecondQuestion({
              question: message.message,
              threadUuid: resolvedThreadUuid ?? threadUuid ?? null,
              currentSessionId: userData?.current_session_id ?? null,
              firstAnswerHadChart: messageHasChart(firstCompletedAnswer),
              firstAnswerFollowupClicked: false,
              conversionGate,
            });
            if (pending?.question) {
              setQuestion(pending.question);
            }
            setTimeout(() => {
              setShowSignUpModal(true);
            }, 500);
          }

          if (botResponse.show_paywall) {
            shouldDrainQueuedMessages = false;
            const conversionGate = conversionGateFromResponse(botResponse);
            setPaywallMessage(botResponse.error || undefined);
            setConversionGateContext(conversionGate);
            setPaywallConversionGateContext(conversionGate);
            setTimeout(() => {
              setShowPaywallModal(true);
            }, 500);
          }

          if (botResponse.show_past_due_modal) {
            shouldDrainQueuedMessages = false;
            setTimeout(() => {
              setShowPastDueModal(true);
            }, 500);
          }

          questions_prepopulated = [message.message];
        } else if (!botResponse || !botResponse.botMessage) {
          response_message = {
            createdAt: null,
            message: "Please try again, there was an unexpected server error.",
            sender: "bot",
            tickerDescription: [],
            answeredTickerIds: [],
            logoUrls: [],
            isError: true,
          };
          questions_prepopulated = [message.message];
        } else {
          response_message = botResponse.botMessage;
          questions_prepopulated = botResponse.questionsPrepop;
        }

        if (isRestoredPendingSecondQuestion && botResponse?.botMessage) {
          trackFrontendEvent(
            "second_question_answer_completed",
            pendingSecondQuestionEventMetadata({}, restoredPendingSecondQuestion),
          );
          restoredPendingSecondQuestionRef.current = null;
        }

        if (botResponse?.skipBotMessage) {
          shouldDrainQueuedMessages = false;
          if (resolvedThreadUuid) {
            clearDispatchRuntimeState(resolvedThreadUuid, {
              clearPreventInput: false,
            });
          } else {
            clearDispatchRuntimeState(null, {
              clearLiveText: false,
              clearPreventInput: false,
            });
          }
          setOpacity(0.0);
          setOpacity(1.0);
          setButtonOpacity(0.999);
          if (resolvedThreadUuid) {
            setThreadPreventQuestionInput(resolvedThreadUuid, false);
          } else {
            setPreventQuestionInput(false);
          }
          return;
        }

        if (botResponse?.shouldShowReview) {
          setTimeout(() => {
            setShowReviewModal(true);
          }, 500);
        }
        if (!botResponse?.shouldShowReview && botResponse?.shouldShowReferralPrompt && botResponse?.referralProgram) {
          setReferralProgram(botResponse.referralProgram);
          setTimeout(() => {
            setShowReferralInviteModal(true);
          }, 700);
        }
        if (
          !botResponse?.shouldShowReview &&
          !botResponse?.shouldShowReferralPrompt &&
          botResponse?.shouldShowServiceTipPrompt &&
          botResponse?.serviceTipPrompt
        ) {
          setServiceTipPrompt(botResponse.serviceTipPrompt);
          setTimeout(() => {
            setShowServiceTipModal(true);
          }, 700);
        }
        if (botResponse?.showTextSetupModal) {
          setTextSetupContext(botResponse.textSetupContext || { source: "chat_text_setup" });
          setTimeout(() => {
            setShowTextSetupModal(true);
          }, 500);
        }
        setUserData({
          ...userData,
          ...{
            show_user_profession: botResponse.showUserProfession,
            user_profession: botResponse.userProfession,
            showAskShareMessage: botResponse.showAskShareMessage,
          },
        });

        if (botResponse.showSaveHomePopup && isMobileIOS()) {
          setShowAddHomeModal(true);
        }

        if (botResponse.showSubscriptionOffer) {
          setTimeout(() => {
            setShowSubOfferModal("offering");
          }, 500);
        }
        setFreeUnlimitedOnly(botResponse.freeUnlimitedOnly);
        setConversionGateContext(conversionGateFromResponse(botResponse));
        setHasShownFreeUnlimitedMessage(
          botResponse.hasShownFreeUnlimitedMessage,
        );

        if (!isLoggedIn && userData.requireSignIn) {
          if (userData.uuidHasAccount) {
            appProps.setRegisterNavMessage(requireLoginMessage);
          } else {
            appProps.setRegisterNavMessage(requireCreateAccountMessage);
          }
          navigate("/register");
        }

        const shouldSuppressResponseActionsForQueuedDispatch =
          shouldDrainQueuedMessages &&
          Boolean(
            resolvedThreadUuid &&
              queuedMessagesForThread(
                queuedMessagesByThreadUuidRef.current,
                resolvedThreadUuid,
              ).some(
                (item) => item?.clientQueueId !== queuedClientQueueId,
              ),
          );
        suppressedResponseActionsForQueue =
          shouldSuppressResponseActionsForQueuedDispatch;

        let timeout = 50;
        setTimeout(() => {
          if (resolvedThreadUuid) {
            setThreadThing(resolvedThreadUuid, true);
            setThreadTestResponseMessage(resolvedThreadUuid, response_message);
            setThreadLoadingResponse(resolvedThreadUuid, false);
            setThreadShowLoadingWaitingMessage(resolvedThreadUuid, 0);
            setThreadLoadingPhaseMessage(resolvedThreadUuid, null);
            setThreadLoadingPhaseDetail(resolvedThreadUuid, null);
            if (!shouldSuppressResponseActionsForQueuedDispatch) {
              setThreadPreventQuestionInput(resolvedThreadUuid, false);
            }
          } else {
            checkThing(true);
            setTestResponseMessage(response_message);
            setLoadingResponse(false);
            setShowLoadingWaitingMessage(0);
            if (!shouldSuppressResponseActionsForQueuedDispatch) {
              setPreventQuestionInput(false);
            }
          }
          setOpacity(0.0);
          setOpacity(1.0);
          setButtonOpacity(0.999);
        }, timeout);

        setTimeout(() => {
          if (resolvedThreadUuid) {
            setResponseFlowForThread(resolvedThreadUuid, (responseFlow) => ({
              ...responseFlow,
              review: true,
            }));
          } else {
            setResponseFlow((responseFlow) => ({
              ...responseFlow,
              review: true,
            }));
          }
        }, timeout);

        if (!shouldSuppressResponseActionsForQueuedDispatch) {
          timeout = timeout + 200;
          setTimeout(() => {
            if (resolvedThreadUuid) {
              setResponseFlowForThread(resolvedThreadUuid, (responseFlow) => ({
                ...responseFlow,
                text: true,
              }));
            } else {
              setResponseFlow((responseFlow) => ({
                ...responseFlow,
                text: true,
              }));
            }
          }, timeout);

          timeout = timeout + 200;
          const recommendedActions = Array.isArray(botResponse?.recommendedActions)
            ? botResponse.recommendedActions
            : [];
          questions_prepopulated.forEach((message_text, idx) => {
            let q = {
              sender: "human",
              isButton: true,
              message: message_text,
            };
            setTimeout(() => {
              if (idx == 0) {
                if (resolvedThreadUuid) {
                  setResponseFlowForThread(
                    resolvedThreadUuid,
                    (responseFlow) => ({
                      ...responseFlow,
                      question1: [true, q.message, null, false, null, recommendedActions[0] || null],
                    }),
                  );
                } else {
                  setResponseFlow((responseFlow) => ({
                    ...responseFlow,
                    question1: [true, q.message, null, false, null, recommendedActions[0] || null],
                  }));
                }
              } else if (resolvedThreadUuid) {
                setResponseFlowForThread(
                  resolvedThreadUuid,
                  (responseFlow) => ({
                    ...responseFlow,
                    question2: [
                      true,
                      q.message,
                      null,
                      botResponse.isAdvanced,
                      botResponse.advancedType,
                      recommendedActions[idx] || null,
                    ],
                  }),
                );
              } else {
                setResponseFlow((responseFlow) => ({
                  ...responseFlow,
                  question2: [
                    true,
                    q.message,
                    null,
                    botResponse.isAdvanced,
                    botResponse.advancedType,
                    recommendedActions[idx] || null,
                  ],
                }));
              }

              if (botResponse.showSignUp) {
                setTimeout(() => {
                  setShowSignUpModal(true);
                }, 500);
              }
            }, timeout);
          });

          timeout = timeout + 200;
          setTimeout(() => {
            if (resolvedThreadUuid) {
              setResponseFlowForThread(resolvedThreadUuid, (responseFlow) => ({
                ...responseFlow,
                regen: true,
              }));
              setThreadPreventQuestionInput(resolvedThreadUuid, false);
            } else {
              setResponseFlow((responseFlow) => ({
                ...responseFlow,
                regen: true,
              }));
              setPreventQuestionInput(false);
            }
          }, timeout);
        }
      })
      .catch((error) => {
        shouldDrainQueuedMessages = false;
        hasReceivedResponse = true;
        console.error("sendChatMessages failed", error);

        const response_message = {
          createdAt: null,
          message: "Please try again, there was an unexpected server error.",
          sender: "bot",
          tickerDescription: [],
          answeredTickerIds: [],
          logoUrls: [],
          isError: true,
        };

        if (threadUuid) {
          setThreadThing(threadUuid, true);
          setThreadTestResponseMessage(threadUuid, response_message);
          clearDispatchRuntimeState(threadUuid);

          setResponseFlowForThread(threadUuid, (responseFlow) => ({
            ...responseFlow,
            question1: [true, message.message, null, false, null],
            question2: [false, responseFlow.question2[1], responseFlow.question2[2], responseFlow.question2[3], responseFlow.question2[4], responseFlow.question2[5]],
            regen: true,
          }));
        } else {
          checkThing(true);
          setTestResponseMessage(response_message);
          clearDispatchRuntimeState(null, { clearLiveText: false });

          setResponseFlow((responseFlow) => ({
            ...responseFlow,
            question1: [true, message.message, null, false, null],
            question2: [false, responseFlow.question2[1], responseFlow.question2[2], responseFlow.question2[3], responseFlow.question2[4], responseFlow.question2[5]],
            regen: true,
          }));
        }

        setOpacity(0.0);
        setOpacity(1.0);
        setButtonOpacity(0.999);
      })
      .finally(() => {
        const result = clearActiveThreadDispatch(
          inFlightSendByThreadRef.current,
          dispatchThreadUuid,
          requestToken,
        );
        if (!result.cleared) return;
        inFlightSendByThreadRef.current = result.registry;
        if (queuedClientQueueId) {
          if (shouldDrainQueuedMessages) {
            completeQueuedMessageInThread(dispatchThreadUuid, queuedClientQueueId);
          } else {
            failQueuedMessageInThread(
              dispatchThreadUuid,
              queuedClientQueueId,
              queuedFailureMessage,
            );
          }
        }
        if (!shouldDrainQueuedMessages || !dispatchThreadUuid) return;
        setTimeout(() => {
          const drained = drainNextQueuedMessageForThread(dispatchThreadUuid);
          if (!drained && suppressedResponseActionsForQueue) {
            setThreadPreventQuestionInput(dispatchThreadUuid, false);
          }
        }, QUEUED_MESSAGE_DRAIN_DELAY_MS);
      });

    return true;
  };

  function drainNextQueuedMessageForThread(threadUuid) {
    if (!threadUuid) return false;
    if (hasActiveThreadDispatch(inFlightSendByThreadRef.current, threadUuid)) {
      return false;
    }

    const nextQueuedMessage = queuedMessagesForThread(
      queuedMessagesByThreadUuidRef.current,
      threadUuid,
    )[0];
    if (!nextQueuedMessage?.clientQueueId) return false;

    const dispatchPayload = nextQueuedMessage.dispatchPayload || {};
    markQueuedMessageInThread(
      threadUuid,
      nextQueuedMessage.clientQueueId,
      QUEUED_MESSAGE_STATUS.SENDING,
      { shouldAnchor: true, queueError: null },
    );

    const dispatched = dispatchUserQuestion(
      dispatchPayload.message ?? nextQueuedMessage.message ?? "",
      dispatchPayload.messageId ?? null,
      dispatchPayload.tickerIds ?? null,
      dispatchPayload.imagesMeta || [],
      {
        ...(dispatchPayload.options || {}),
        fromQueue: true,
        suppressLocalUserAppend: false,
        clientQueueId: nextQueuedMessage.clientQueueId,
      },
    );

    if (dispatched === false) {
      failQueuedMessageInThread(
        threadUuid,
        nextQueuedMessage.clientQueueId,
        "Message could not be sent.",
      );
      return false;
    }

    return true;
  }

  function retryFailedMessageForThread(threadUuid, targetId, overrides = {}) {
    if (!threadUuid || targetId == null || targetId === "") return false;
    if (threadHasActiveDispatch(threadUuid)) return false;

    const threadMessages = Array.isArray(
      messagesByThreadUuidRef.current?.[threadUuid],
    )
      ? messagesByThreadUuidRef.current[threadUuid]
      : [];
    const failedMessage = threadMessages.find((message) =>
      localMessageMatches(message, targetId),
    );
    if (!failedMessage) return false;

    const dispatchPayload = failedMessage.dispatchPayload || {};
    const retryClientQueueId =
      failedMessage.clientQueueId ||
      failedMessage.id ||
      failedMessage.uuid ||
      targetId;
    const overrideMessageText =
      overrides && Object.prototype.hasOwnProperty.call(overrides, "message")
        ? String(overrides.message || "").trim()
        : null;
    const retryMessageText =
      overrideMessageText ||
      (dispatchPayload.message ?? failedMessage.message ?? "");
    if (!hasText(retryMessageText)) return false;
    const retryDispatchPayload = {
      message: retryMessageText,
      messageId: dispatchPayload.messageId ?? null,
      tickerIds: dispatchPayload.tickerIds ?? null,
      imagesMeta: dispatchPayload.imagesMeta || [],
      options: dispatchPayload.options || {},
    };
    const retryOptions = {
      ...(dispatchPayload.options || {}),
      fromQueue: true,
      suppressLocalUserAppend: true,
      clientQueueId: retryClientQueueId,
      overrideMessage: {
        ...failedMessage,
        clientQueueId: retryClientQueueId,
        queueStatus: QUEUED_MESSAGE_STATUS.SENDING,
        queueError: null,
        message: retryMessageText,
        dispatchPayload: retryDispatchPayload,
        shouldAnchor: true,
      },
    };

    patchLocalMessageSendState(threadUuid, retryClientQueueId, {
      clientQueueId: retryClientQueueId,
      queueStatus: QUEUED_MESSAGE_STATUS.SENDING,
      queueError: null,
      message: retryMessageText,
      dispatchPayload: retryDispatchPayload,
      shouldAnchor: true,
    });

    const dispatched = dispatchUserQuestion(
      retryMessageText,
      retryDispatchPayload.messageId,
      retryDispatchPayload.tickerIds,
      retryDispatchPayload.imagesMeta,
      retryOptions,
    );

    if (dispatched === false) {
      failQueuedMessageInThread(
        threadUuid,
        retryClientQueueId,
        "Message could not be retried.",
      );
      return false;
    }

    return true;
  }

  function submitEditedQueuedMessageForThread(threadUuid, targetId, newText) {
    const cleaned = (newText ?? "").trim();
    if (!threadUuid || targetId == null || targetId === "" || !cleaned) {
      return false;
    }

    const threadMessages = Array.isArray(
      messagesByThreadUuidRef.current?.[threadUuid],
    )
      ? messagesByThreadUuidRef.current[threadUuid]
      : [];
    const message = threadMessages.find((item) =>
      localMessageMatches(item, targetId),
    );

    if (message?.queueStatus === QUEUED_MESSAGE_STATUS.QUEUED) {
      const clientQueueId = message.clientQueueId || targetId;
      return editQueuedMessageForThread(threadUuid, clientQueueId, cleaned);
    }

    if (message?.queueStatus === QUEUED_MESSAGE_STATUS.FAILED) {
      return retryFailedMessageForThread(threadUuid, targetId, {
        message: cleaned,
      });
    }

    return false;
  }

  const sendUserQuestion = (
    q = null,
    messageId = null,
    ticker_ids = null,
    imagesMeta = [],
    options = {},
  ) => dispatchUserQuestion(q, messageId, ticker_ids, imagesMeta, options);

  return {
    handleAbortResponse,
    editQueuedMessageForThread,
    moveQueuedMessagesByThreadUuidRef,
    removeQueuedMessageFromThread,
    retryFailedMessageForThread,
    submitEditedQueuedMessageForThread,
    sendUserQuestion,
  };
};

export default useChatDispatch;
